import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { open, close } from '@/store/uiSlice';
import { setScheduleDate, setModalContextDate } from '@/store/modalSlice';

/**
 * Modal keys that are tracked in the URL as ?modal=<key>.
 * 'history' and 'admin' are AppBar toggles that don't need URL tracking.
 */
const TRACKED_MODALS = [
    'menu',
    'user',
    'mailbox',
    'filter',
    'event',
    'absent',
    'agenda',
    'files',
    'schedule',
    'categoryRequest',
    'categoryRequestsReview',
    'scheduleTemplateSets',
    'changeRequests',
    'yearSummary',
    'personalForecast',
    'handbook',
] as const;

type TrackedModal = (typeof TRACKED_MODALS)[number];

function isTracked(key: string): key is TrackedModal {
    return TRACKED_MODALS.includes(key as TrackedModal);
}

/** Modals that carry a date in the URL for state preservation across reloads. */
const DATE_CONTEXT_MODALS: readonly TrackedModal[] = [
    'schedule',
    'agenda',
    'files',
    'absent',
    'changeRequests',
];

/**
 * Bidirectional sync between Redux ui.open state and ?modal=<key>&date=<YYYY-MM-DD> URL params.
 *
 * Redux → URL: when a modal opens in Redux, push ?modal=<key> (+ &date=<date> for date-aware
 *              modals) so back-navigation closes it and the URL encodes the context.
 * URL → Redux: when the URL changes (e.g. browser back or direct load), update Redux accordingly
 *              and restore the date context (schedule date on reload).
 */
export function useModalNavigation() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const uiOpen = useAppSelector((state) => state.ui.open);

    // Date context from the modal slice
    const scheduleDate = useAppSelector((state) => state.modal.scheduleDate);
    const modalContextDate = useAppSelector((state) => state.modal.modalContextDate);

    const urlModal = searchParams.get('modal') as TrackedModal | null;
    const urlDate = searchParams.get('date');

    const updatingFromUrl = useRef(false);
    const updatingFromRedux = useRef(false);

    // Updated synchronously during render — always current when effects run
    const locationRef = useRef(location);
    locationRef.current = location;

    // ── URL → Redux ──────────────────────────────────────────────────────────
    // Browser back/forward or direct URL load — sync to Redux
    useEffect(() => {
        if (updatingFromRedux.current) return;
        updatingFromUrl.current = true;

        if (urlModal && isTracked(urlModal)) {
            if (!uiOpen[urlModal]) {
                dispatch(open(urlModal));
            }
            // Restore date context so the modal has the correct date on reload
            if (urlDate) {
                if (urlModal === 'schedule') {
                    dispatch(setScheduleDate(urlDate));
                } else {
                    dispatch(setModalContextDate(urlDate));
                }
            }
        } else {
            // No modal in URL — close any tracked modal that's open
            TRACKED_MODALS.forEach((key) => {
                if (uiOpen[key]) dispatch(close(key));
            });
        }

        updatingFromUrl.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlModal, urlDate]);

    // ── Redux → URL ──────────────────────────────────────────────────────────
    // A modal was opened/closed in Redux — sync to URL
    useEffect(() => {
        if (updatingFromUrl.current) return;

        const openModal = TRACKED_MODALS.find((key) => uiOpen[key]);

        if (openModal) {
            const currentParams = new URLSearchParams(locationRef.current.search);
            const currentUrlModal = currentParams.get('modal');
            const currentUrlDate = currentParams.get('date');

            // Determine the date to put in the URL for this modal
            let targetDate: string | null = null;
            if (openModal === 'schedule') {
                targetDate = scheduleDate || null;
            } else if (DATE_CONTEXT_MODALS.includes(openModal)) {
                targetDate = modalContextDate || null;
            }

            const urlMatchesState = currentUrlModal === openModal && currentUrlDate === targetDate;

            if (!urlMatchesState) {
                let url = `?modal=${openModal}`;
                if (targetDate) url += `&date=${targetDate}`;
                navigate(url, { replace: currentUrlModal !== null });
            }
        } else {
            // No modal open — remove modal params from the URL so a reload doesn't reopen it
            const currentParams = new URLSearchParams(locationRef.current.search);
            if (currentParams.has('modal')) {
                navigate(locationRef.current.pathname, { replace: true });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        TRACKED_MODALS.map((k) => uiOpen[k]).join(','),
        scheduleDate,
        modalContextDate,
    ]);
}
